import React, {
  useState, useRef, useEffect, useLayoutEffect,
} from "react"
import {
  Tooltip, message, Modal, Button,
} from "antd"
import { PaperClipOutlined, ExclamationCircleFilled } from "@ant-design/icons"
import Lottie from "react-lottie"
import ReactMarkdown from "react-markdown"
import FileUploadModal from "./FileUploadModal"
import exploreUA from "../../assets/images/exploreUA.png"
import aboutUA from "../../assets/images/aboutUA.png"
import send from "../../assets/images/send.png"
// import backArrow from "../../assets/images/backArrow.png"
import HiAnimation from "../../assets/images/HiAnimation.json"
import staticAnimation from "../../assets/images/staticAnimation.json"
import Loading from "../../assets/images/Loading.json"
import sad from "../../assets/images/sad.json"
import commonStrings from "../commonStrings"
import ConversationHistory from "./ConversationHistory"

import "animate.css"
import editImage from "../../assets/images/edit.png"
import copyImage from "../../assets/images/copy.png"
import copiedImage from "../../assets/images/copied.png"
import VoiceInput from "./shared/VoiceInput"
import recording from "../../assets/images/recording-dot.png"
import csvIcon from "../../assets/images/csv-icon.png"
import useProgramMatrixWizard from "./ProgramMatrixChatWizard/useProgramMatrixWizard"
import WizardMessage from "./ProgramMatrixChatWizard/WizardMessage"
import persistMessage from "./chatMessagePersistence"

const getModule = () => {
  const { pathname } = window.location
  if (pathname.includes("/sustainability_rubrics/")) return "sustainability_rubric"
  if (pathname.includes("/implementation_rubrics/")) return "implementation_rubric"
  if (pathname.includes("/program_matrices")) return "program_matrix"
  if (document.querySelector("[data-react-class=\"SchoolDashboard\"]")) return "school_dashboard"
  if (document.querySelector("[data-react-class=\"ProgramDashboard\"]")) return "program_dashboard"
  return "general"
}

const trackChatBot = (eventName, params = {}) => {
  if (typeof window.gtag !== "function") return
  window.gtag("event", eventName, {
    event_category: "ChatBot",
    module: getModule(),
    ...params,
  })
}

// Program Matrix only ingests CSV/XLS/XLSX (see useProgramMatrixWizard's isValidFileType);
// Sustainability only ingests PDF/JPG/JPEG/PNG/GIF (see FileUploadModal's isValidFileType) — so
// the extension alone already rules out one module or the other. That's a stronger, deterministic
// signal than either "which page is this attach icon on" or GPT's read of the file's content,
// both of which can suggest a module the file could never actually be processed by (e.g. a PDF
// *about* Program Matrix getting classified as Program Matrix content).
const EXTENSION_MODULE_MAP = {
  csv: "program_matrix",
  xls: "program_matrix",
  xlsx: "program_matrix",
  pdf: "sustainability",
  jpg: "sustainability",
  jpeg: "sustainability",
  png: "sustainability",
  gif: "sustainability",
}

const getModuleForExtension = (fileName) => {
  const ext = (fileName || "").split(".").pop().toLowerCase()
  return EXTENSION_MODULE_MAP[ext] || null
}

const MODULE_LABELS = {
  sustainability: "Sustainability Rubric",
  program_matrix: "Program Matrix",
}

// Lets typed free text answer a pending module-classification Yes/No card (see
// classifyAndRouteUpload/askPageModuleConfirmation) the same way clicking one of its two options
// would, instead of the text falling through to semantic search and coming back empty. Only
// resolves when exactly one module's pattern matches — an ambiguous or unrelated message still
// requires a button click.
const MODULE_FREE_TEXT_PATTERNS = {
  sustainability: /sustainability|\brubric\b/i,
  program_matrix: /program matrix|\bmatrix\b/i,
}

// A bare "yes"/"no" (no module named) answers relative to which option the card is actually
// offering — its primary-styled button is the guessed module, so "yes" confirms that one and "no"
// picks the other, same as clicking would.
const AFFIRMATIVE_PATTERN = /^(yes|yeah|yep|yup|correct|right|sure|ok|okay|confirm)\b/i
const NEGATIVE_PATTERN = /^(no|nope|nah|incorrect|wrong)\b/i

const resolveModuleFromFreeText = (text, options) => {
  const matches = Object.keys(MODULE_FREE_TEXT_PATTERNS)
    .filter((moduleName) => MODULE_FREE_TEXT_PATTERNS[moduleName].test(text))
  if (matches.length === 1) return matches[0]
  if (matches.length > 0 || !options) return null

  const trimmed = text.trim()
  const primaryOption = options.find((o) => o.style === "primary")
  const otherOption = options.find((o) => o.style !== "primary")
  if (primaryOption && AFFIRMATIVE_PATTERN.test(trimmed)) return primaryOption.value
  if (otherOption && NEGATIVE_PATTERN.test(trimmed)) return otherOption.value
  return null
}

const ChatBotUI = ({
  setHasInteracted,
  setActiveView,
  activeView,
  setConvMessages,
  convMessages,
  shouldAutoOpen,
  onChatBotOpen,
  setMessages,
  messages,
  category,
  isRubricSubmitted,
  currentDraftRubricId,
  canCreateRubric,
  onLoadingChange,
  onTypingChange,
}) => {
  const [userInput, setUserInput] = useState("")
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const messagesEndRef = useRef(null)
  const [hasAcceptedTermsBefore, setHasAcceptedTermsBefore] = useState(
    () => localStorage.getItem("uaAcceptedTerms") === "true",
  )

  const [hasNoResponse, setHasNoResponse] = useState(false)
  const [copiedMessageId, setCopiedMessageId] = useState(null)
  const [showCopiedTooltip, setShowCopiedTooltip] = useState({})

  const [isTypingAnimation, setIsTypingAnimation] = useState(false)
  const [typingMessageIndex, setTypingMessageIndex] = useState(-1)
  const [displayText, setDisplayText] = useState("")
  const typingIntervalRef = useRef(null)
  const [isVoiceRecording, setIsVoiceRecording] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [isUploadingEvidence, setIsUploadingEvidence] = useState(false)
  const [evidenceInitialFile, setEvidenceInitialFile] = useState(null)
  const [locallyCreatedDraftRubricId, setLocallyCreatedDraftRubricId] = useState(null)
  const [isCreatingRubric, setIsCreatingRubric] = useState(false)
  const [showSubmittedUploadConfirm, setShowSubmittedUploadConfirm] = useState(false)
  const evidenceFileInputRef = useRef(null)
  const effectiveDraftRubricId = currentDraftRubricId || locallyCreatedDraftRubricId
  const willCreateNewRubric = !effectiveDraftRubricId && canCreateRubric

  // Off-page module-attach flow: every file pick asks which module it's for (on-page or off),
  // fresh each time — nothing here is cached across attaches. This only carries the CURRENT
  // attach's confirmed module for the one tick between confirming "Program Matrix" and pmWizard
  // (below) re-rendering as active, since handleFileAttach isn't callable in that same tick.
  const [confirmedOffPageModule, setConfirmedOffPageModule] = useState(null)
  const [isClassifyingUpload, setIsClassifyingUpload] = useState(false)
  // Holds a file confirmed for Program Matrix until pmWizard (below) re-renders as active —
  // isActive flips via state, so handleFileAttach isn't callable in the same tick as the confirm.
  const pendingProgramMatrixFileRef = useRef(null)
  // Bumped on every confirmed Program Matrix attach, including a SECOND (or later) off-page
  // attach in the same session — confirmedOffPageModule is already "program_matrix" by then, so
  // setConfirmedOffPageModule below is a same-value no-op React won't re-render for, and the
  // effect that consumes pendingProgramMatrixFileRef would otherwise never re-fire (the file
  // — and the whole attach — would silently go nowhere; see the effect below).
  const [programMatrixAttachTick, setProgramMatrixAttachTick] = useState(0)
  // Holds the file awaiting a module-classification confirm response. Kept in a ref rather than
  // on the message itself — messages get JSON.stringify'd into localStorage (conversation
  // history), which would silently mangle a raw File object into "{}".
  const pendingClassificationFileRef = useRef(null)
  // handleSend (below) needs to call handleModuleClassificationRespond, which is defined further
  // down (it depends on routeToOffPageModule/routeToSustainability/routeToProgramMatrix) — this
  // ref lets it reach that function without a no-use-before-define violation.
  const handleModuleClassificationRespondRef = useRef(null)

  // Pass loading state up to parent
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoading)
    }
  }, [isLoading, onLoadingChange])

  useEffect(() => {
    if (onTypingChange) {
      onTypingChange(isTypingAnimation)
    }
  }, [isTypingAnimation, onTypingChange])
  // Message editing state
  const [editingMessageIndex, setEditingMessageIndex] = useState(null)
  const [editText, setEditText] = useState("")
  const [editedMessages, setEditedMessages] = useState(new Set())

  // Scroll to bottom function (moved before usage)
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }

  // Create typing animation function
  const animateTyping = (fullText, messageIndex, speed = 9) => {
    // Clear any existing animation
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current)
    }

    // Set animation state
    setIsTypingAnimation(true)
    setTypingMessageIndex(messageIndex)
    setDisplayText("")

    let currentIndex = 0

    typingIntervalRef.current = setInterval(() => {
      if (currentIndex < fullText.length) {
        const nextChar = fullText[currentIndex]

        setDisplayText((prev) => prev + nextChar)
        currentIndex += 1

        // Add pause at punctuation marks for more realistic timing
        if ([".", "!", "?", ":"].includes(nextChar)) {
          // Brief pause for punctuation
          setTimeout(() => {}, 200)
        }
      } else {
        // Animation complete
        clearInterval(typingIntervalRef.current)
        setIsTypingAnimation(false)
        setTypingMessageIndex(-1)
        setDisplayText("")
      }
    }, speed)
  }

  // Enhanced scroll behavior during animation
  useEffect(() => {
    if (isTypingAnimation) {
      // Scroll during animation with a slight delay for smooth experience
      const scrollTimer = setTimeout(() => {
        scrollToBottom()
      }, 100)

      return () => clearTimeout(scrollTimer)
    }
    return undefined
  }, [displayText, isTypingAnimation])

  // Cleanup animation on component unmount
  useEffect(
    () => () => {
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current)
      }
    },
    [],
  )

  // Check if we're on program_matrices page
  const isProgramMatricesPage = () => window.location.pathname.includes("/program_matrices")

  // Convert API message format to component format
  const convertApiMessageToComponentFormat = (message) => {
    if (message.role === "user") {
      return { role: "user", content: message.content }
    }
    if (message.role === "assistant") {
      return { role: "bot", content: message.content, isDefault: false }
    }
    if (message.role === "system") {
      return null // Filter out system messages
    }
    return message // Fallback for other formats
  }

  // Filter out system messages from conversation and convert format
  const filterAndConvertMessages = (conversation) => {
    if (!Array.isArray(conversation)) return []
    return conversation
      .filter((msg) => msg.role !== "system")
      .map(convertApiMessageToComponentFormat)
      .filter((msg) => msg !== null)
  }

  // Save conversation to localStorage whenever messages change
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem("conversation_history", JSON.stringify(messages))
    }
  }, [messages])

  useLayoutEffect(() => {
    if (convMessages) {
      APIClient.get(`/conversations/${convMessages}/messages`, (response) => {
        // Filter out system messages and convert format
        const filteredResponse = filterAndConvertMessages(response)
        setMessages(filteredResponse)
        localStorage.setItem(
          "conversation_history",
          JSON.stringify(filteredResponse),
        )
      })
    }
  }, [convMessages])

  const predefinedQuestions = {
    about: [
      "What is Urban Assembly?",
      "Urban Assembly Schools?",
      "Programs offered in Urban Assembly?",
    ],
    explore: [
      "How can I apply here?",
      "What support is available?",
      "Are internships available for students?",
      "Are there training programs for teachers?",
    ],
  }

  const parseRubricUrl = () => {
    const { pathname } = window.location
    const searchParams = new URLSearchParams(window.location.search)

    const rubricPattern = /^\/([a-zA-Z]+)_rubrics\/(\d+)$/
    const rubricMatch = pathname.match(rubricPattern)
    /* eslint-disable camelcase */
    if (rubricMatch) {
      return {
        component: rubricMatch[1],
        rubric_id: parseInt(rubricMatch[2], 10),
      }
    }

    if (
      pathname.startsWith("/program_matrix")
      && searchParams.has("activityId")
    ) {
      return {
        component: "program_matrix",
        rubric_id: parseInt(searchParams.get("activityId"), 10),
      }
    }

    if (pathname.startsWith("/program_matrices")) {
      return {
        component: "program_matrices",
        rubric_id: null,
      }
    }

    return {
      component: null,
      rubric_id: null,
    }
  }
  /* eslint-disable camelcase */

  const parsedRubric = parseRubricUrl()
  const isSustainabilityModule = parsedRubric.component === "sustainability"
    || category === "sustainability"
  const isProgramMatrixModule = parsedRubric.component === "program_matrix"
    || parsedRubric.component === "program_matrices"

  // parsedRubric.rubric_id is only ever populated when actually on a /sustainability_rubrics/:id
  // URL — off-page (confirmed via the classify-and-route flow), there's no viewed rubric to defer
  // to, so always fall back to the resolved draft regardless of isRubricSubmitted.
  const targetRubricId = isSustainabilityModule && parsedRubric.rubric_id
    ? (isRubricSubmitted ? effectiveDraftRubricId : parsedRubric.rubric_id)
    : effectiveDraftRubricId

  // Mirrors ProgramMatrix/Index.jsx#areGradesAndPopulationsConfigured so the chatbot's
  // attachment button stays in sync with the "Upload File" button on the Program Matrix page.
  const areGradesAndPopulationsConfigured = () => {
    const selectedGrades = window.gon
      && window.gon.CURRENT_SCHOOL_PROGRAM_MATRIX
      && window.gon.CURRENT_SCHOOL_PROGRAM_MATRIX.selected_grades

    if (!selectedGrades || selectedGrades.length === 0) {
      return false
    }

    return selectedGrades.every(
      (grade) => grade.grade && grade.student_population && grade.student_population > 0,
    )
  }

  // Off-page confirmation activates the wizard the same way being on the Program Matrix page
  // does — areGradesAndPopulationsConfigured() isn't checked here for the off-page case since,
  // until the user confirms Program Matrix, we don't yet know that check is even relevant
  // (see routeToProgramMatrix, which checks it explicitly once confirmed).
  const pmWizardActive = isProgramMatrixModule || confirmedOffPageModule === "program_matrix"
  const pmWizard = useProgramMatrixWizard({
    isActive: pmWizardActive,
    setMessages,
    messages,
  })

  const isAttachmentDisabled = isLoading
    || isTypingAnimation
    || isCreatingRubric
    || isClassifyingUpload
    || pmWizard.isBusy
    || (isProgramMatrixModule && !areGradesAndPopulationsConfigured())

  // Pushes a message into the visible transcript and persists it via the shared queue — for the
  // evidence-upload and module-classification sub-flows, which (unlike the regular Q&A flow)
  // don't get persisted as a side effect of their own API calls.
  const pushMessage = (msg) => {
    setMessages((prev) => [...prev, msg])
    persistMessage(msg)
  }

  const attachmentButtonTitle = isClassifyingUpload
    ? "Rei is figuring out what this document is for"
    : pmWizard.isBusy
      ? "Rei is still analyzing your document"
      : isProgramMatrixModule && !areGradesAndPopulationsConfigured()
        ? "Please configure School Grade Levels and populations first"
        : "Attach evidence file"

  useEffect(() => {
    if (
      isProgramMatrixModule
      && hasAcceptedTermsBefore
      && messages.length === 0
    ) {
      setMessages([
        {
          role: "bot",
          isDefault: true,
          content:
            "Upload a document that describes your activities, and I'll help you add each one to your Program Matrix.",
        },
      ])
      setActiveView("chat")
    }
  }, [isProgramMatrixModule, hasAcceptedTermsBefore])

  const handleActivityNavigation = (href) => {
    if (isProgramMatricesPage()) {
      // Store navigation state only for program_matrices
      const currentState = {
        messages,
        userInput,
        activeView,
        timestamp: Date.now(),
      }

      localStorage.setItem(
        "program_matrices_navigation_state",
        JSON.stringify(currentState),
      )

      // Set flag to auto-open chatbot when returning
      localStorage.setItem("program_matrices_auto_open", "true")
    }

    // Navigate to the activity page
    window.location.href = href
  }

  // Check if returning from navigation to activity page
  useEffect(() => {
    // Same reasoning as ChatBotLauncher's conversation-resumption effect: only fill in state on a
    // genuinely fresh mount. Skipping this once `messages` is already populated avoids stomping
    // whatever ChatBotLauncher already restored (or the live session already built up) with this
    // separate, possibly-older navigation snapshot.
    if (isProgramMatricesPage() && messages.length === 0) {
      const navigationState = localStorage.getItem(
        "program_matrices_navigation_state",
      )
      const shouldAutoOpen = localStorage.getItem("program_matrices_auto_open") === "true"

      if (navigationState) {
        const state = JSON.parse(navigationState)
        // Only restore if it's recent (within 5 minutes)
        if (Date.now() - state.timestamp < 5 * 60 * 1000) {
          setMessages(state.messages || [])
          setUserInput(state.userInput || "")
          setActiveView(state.activeView || "chat")

          // Auto-open the chatbot if returning from activity navigation
          if (shouldAutoOpen && onChatBotOpen) {
            onChatBotOpen()
          }
        }

        // Always clear the navigation state and auto-open flag
        localStorage.removeItem("program_matrices_navigation_state")
        localStorage.removeItem("program_matrices_auto_open")
      }
    }
  }, [window.location.pathname])
  const handleCancelEdit = () => {
    setEditingMessageIndex(null)
    setEditText("")
  }
  // Auto-open chatbot if shouldAutoOpen prop is true
  useEffect(() => {
    if (shouldAutoOpen && onChatBotOpen) {
      onChatBotOpen()
    }
  }, [shouldAutoOpen, onChatBotOpen])

  const handleQuestionClick = async (question) => {
    trackChatBot("predefined_question_clicked", { question_text: question })
    // Cancel any ongoing edit when clicking a question
    if (editingMessageIndex !== null) {
      handleCancelEdit()
    }

    setActiveView("chat")
    setHasNoResponse(false)
    setMessages((prev) => [...prev, { role: "user", content: question }])
    setIsLoading(true)

    // Retrieve conversation from appropriate localStorage
    let conversation = []
    let conversationId = null
    try {
      const storageKey = "conversation_history"
      const idKey = "conversation_id"

      const storedHistory = localStorage.getItem(storageKey)
      const storedId = localStorage.getItem(idKey)

      if (storedHistory) conversation = JSON.parse(storedHistory)
      if (storedId) conversationId = storedId
    } catch (e) {
      console.warn("Failed to parse conversation from localStorage:", e)
    }

    APIClient.post(
      "/semantic_search",
      {
        query: question,
        temperature: 0.6,
        conversation_history: conversation,
        conversation_id: conversationId,
      },
      (response) => {
        setIsLoading(false)

        if (response && response.answer) {
          if (response.is_chunked && response.chunked_response) {
            // Handle chunked response with typing animation
            response.chunked_response.forEach((chunk, index) => {
              setTimeout(() => {
                const botResponse = {
                  role: "bot",
                  content: "", // Initialize with empty content for animation
                  fullContent: chunk, // Store the full content separately
                  isDefault: false,
                  chunkIndex: index,
                }

                // Add message to array first
                setMessages((prev) => {
                  const newMessages = [...prev, botResponse]
                  // Start typing animation for the newly added message immediately
                  animateTyping(chunk, newMessages.length - 1)
                  return newMessages
                })
              }, index * 1200) // 1.2 second delay between chunks
            })
          } else {
            // Handle single response with typing animation
            let botResponse
            if (
              response.answer.includes(
                "Contact Support: rsptech@urbanassembly.org",
              )
            ) {
              botResponse = {
                role: "bot",
                content: response.answer, // Keep full content for non-animated messages
                isDefault: true,
              }
              setHasNoResponse(true)
              setMessages((prev) => [...prev, botResponse])
            } else {
              botResponse = {
                role: "bot",
                content: "", // Initialize with empty content for animation
                fullContent: response.answer, // Store the full content separately
                isDefault: false,
              }

              // Add message and start typing animation immediately
              setMessages((prev) => {
                const newMessages = [...prev, botResponse]
                // Start typing animation for the newly added message immediately
                animateTyping(response.answer, newMessages.length - 1)
                return newMessages
              })
            }
          }
        } else {
          const errorResponse = {
            role: "bot",
            content: `I couldn't find information about "${question}". Please try another question.`,
            isDefault: true,
          }
          setMessages((prev) => [...prev, errorResponse])
          setHasNoResponse(true)
        }

        setActiveView("chat")

        // Save to appropriate localStorage - filter and convert messages first
        if (response.updated_conversation) {
          const filteredConversation = filterAndConvertMessages(
            response.updated_conversation,
          )
          const storageKey = "conversation_history"

          localStorage.setItem(
            storageKey,
            JSON.stringify(filteredConversation),
          )
        }

        if (response.conversation_id) {
          const idKey = "conversation_id"

          localStorage.setItem(idKey, response.conversation_id)
        }
      },
      (error) => {
        console.error("API error:", error)
        const errorResponse = {
          role: "bot",
          content:
            "Sorry, I'm having trouble connecting to the knowledge base. Please try again later.",
          isDefault: true,
        }
        setMessages((prev) => [...prev, errorResponse])
        setHasNoResponse(true)
        setIsLoading(false)
      },
    )
  }

  const handleSend = () => {
    if (!userInput.trim()) return

    // A module-classification Yes/No card is awaiting a response — try to resolve it from typed
    // text before falling through to pmWizard/semantic search (see resolveModuleFromFreeText).
    if (pendingClassificationFileRef.current) {
      const text = userInput.trim()
      let pendingIndex = -1
      for (let i = messages.length - 1; i >= 0; i -= 1) {
        if (
          messages[i].type === "single_choice"
          && messages[i].wizardMeta
          && messages[i].wizardMeta.moduleClassification
          && !messages[i].answered
        ) {
          pendingIndex = i
          break
        }
      }
      const pendingOptions = pendingIndex !== -1
        ? messages[pendingIndex].wizardMeta.options
        : null
      const resolvedModule = resolveModuleFromFreeText(text, pendingOptions)
      if (resolvedModule) {
        if (pendingIndex !== -1) {
          const option = messages[pendingIndex].wizardMeta.options
            .find((o) => o.value === resolvedModule)
          setUserInput("")
          handleModuleClassificationRespondRef.current(pendingIndex, option, text)
          return
        }
      }
    }

    if (pmWizard.isAwaitingFreeText) {
      const text = userInput.trim()
      setUserInput("")
      pmWizard.submitFreeText(text)
      return
    }

    trackChatBot("message_sent", { message_length: userInput.trim().length })
    // Cancel any ongoing edit when sending a new message
    if (editingMessageIndex !== null) {
      handleCancelEdit()
    }

    /* eslint-disable camelcase */
    const { component, rubric_id } = parseRubricUrl()
    /* eslint-disable camelcase */
    const isProgramMatrices = component === "program_matrices"

    setMessages((prev) => [...prev, { role: "user", content: userInput }])
    setUserInput("")
    setIsLoading(true)
    setHasNoResponse(false)
    setActiveView("chat")

    // Retrieve conversation from localStorage
    let conversation = []
    let conversationId = null
    try {
      const storageKey = "conversation_history"
      const idKey = "conversation_id"

      const storedHistory = localStorage.getItem(storageKey)
      const storedId = localStorage.getItem(idKey)

      if (storedHistory) conversation = JSON.parse(storedHistory)
      if (storedId) conversationId = storedId
    } catch (e) {
      console.warn("Failed to parse conversation from localStorage:", e)
    }

    /* eslint-disable camelcase */
    // Determine endpoint based on component presence
    const endpoint = (component && component !== "program_matrices") || isProgramMatrices
      ? "/semantic_search_SEL"
      : "/semantic_search"

    // Build payload based on the context
    let payload
    if (isProgramMatrices) {
      // Program matrices page - include component but no rubric_id
      payload = {
        query: userInput,
        component: category === "general" ? component : category,
        conversation_history: conversation,
        conversation_id: conversationId,
      }
    } else if (component && rubric_id) {
      // Specific rubric pages like /sustainability_rubrics/2659 - include both
      payload = {
        query: userInput,
        component: category === "general" ? component : category,
        rubric_id,
        conversation_history: conversation,
        conversation_id: conversationId,
      }
    } else {
      // General semantic search - no SEL-specific parameters
      payload = {
        query: userInput,
        temperature: 0.6,
        conversation_history: conversation,
        conversation_id: conversationId,
      }
    }
    /* eslint-enable camelcase */

    APIClient.post(
      endpoint,
      payload,
      (response) => {
        setIsLoading(false)

        if (response && response.answer) {
          if (response.is_chunked && response.chunked_response) {
            // Handle chunked response with typing animation
            response.chunked_response.forEach((chunk, index) => {
              setTimeout(() => {
                const botResponse = {
                  role: "bot",
                  content: "", // Initialize with empty content for animation
                  fullContent: chunk, // Store the full content separately
                  isDefault: false,
                  chunkIndex: index,
                }

                // Add message to array first
                setMessages((prev) => {
                  const newMessages = [...prev, botResponse]
                  // Start typing animation for the newly added message immediately
                  animateTyping(chunk, newMessages.length - 1)
                  return newMessages
                })
              }, index * 1200) // 1.2 second delay between chunks
            })
          } else {
            // Handle single response with typing animation
            let botResponse
            if (
              response.answer.includes(
                "Contact Support: rsptech@urbanassembly.org",
              )
            ) {
              botResponse = {
                role: "bot",
                content: response.answer, // Keep full content for non-animated messages
                isDefault: true,
              }
              setHasNoResponse(true)
              setMessages((prev) => [...prev, botResponse])
            } else {
              botResponse = {
                role: "bot",
                content: "", // Initialize with empty content for animation
                fullContent: response.answer, // Store the full content separately
                isDefault: false,
              }

              // Add message and start typing animation immediately
              setMessages((prev) => {
                const newMessages = [...prev, botResponse]
                // Start typing animation for the newly added message immediately
                animateTyping(response.answer, newMessages.length - 1)
                return newMessages
              })
            }
          }
        } else {
          const errorResponse = {
            role: "bot",
            content: `I couldn't find information about "${userInput}". Please try another question.`,
            isDefault: true,
          }
          setMessages((prev) => [...prev, errorResponse])
          setHasNoResponse(true)
        }

        setActiveView("chat")

        // Save to appropriate localStorage - filter and convert messages first
        if (response.updated_conversation) {
          const filteredConversation = filterAndConvertMessages(
            response.updated_conversation,
          )
          const storageKey = "conversation_history"

          localStorage.setItem(
            storageKey,
            JSON.stringify(filteredConversation),
          )
        }

        if (response.conversation_id) {
          const idKey = "conversation_id"

          localStorage.setItem(idKey, response.conversation_id)
        }
      },
      (error) => {
        console.error("API error:", error)
        const errorResponse = {
          role: "bot",
          content:
            "Sorry, I'm having trouble connecting to the knowledge base. Please try again later.",
          isDefault: true,
        }
        setMessages((prev) => [...prev, errorResponse])
        setHasNoResponse(true)
        setIsLoading(false)
      },
    )
  }

  const handleEvidenceSubmit = (formData, analysisResult) => {
    trackChatBot("evidence_uploaded")
    setIsUploadingEvidence(true)
    APIClient.post(
      "/evidence/upload_from_chatbot",
      formData,
      (response) => {
        const rubricId = targetRubricId

        if (!response || !response.id || !rubricId) {
          setIsUploadingEvidence(false)
          setShowUploadModal(false)
          message.success("Evidence uploaded successfully!")
          return
        }

        const evidenceRating = formData.get("evidence_rating")
        const indicatorRating = evidenceRating !== null
          ? parseInt(evidenceRating, 10) - 1
          : response.rating

        // Only auto-fill when the indicator has no justification yet — never
        // overwrite a justification the user already wrote for prior evidence.
        const aiJustification = analysisResult
          && (analysisResult.rating_reasoning || analysisResult.reasoning)
        const explanationToSave = response.explanation || aiJustification || ""
        const justificationNeedsManualEntry = !response.explanation && !aiJustification

        APIClient.put(
          `/sustainability_rubrics/${rubricId}/sustainability_rubric_indicators/${response.id}`,
          { rating: indicatorRating, explanation: explanationToSave },
          (freshIndicator) => {
            setIsUploadingEvidence(false)
            setShowUploadModal(false)

            if (isRubricSubmitted) {
              localStorage.setItem("evidence_upload_success", "true")
              if (justificationNeedsManualEntry) {
                localStorage.setItem("evidence_justification_pending", "true")
              }
              window.location.href = `/sustainability_rubrics/${rubricId}`
              return
            }

            message.success("Evidence uploaded successfully!")
            if (justificationNeedsManualEntry) {
              message.warning(
                "Rei could not generate a justification for this rating — please add one in the Justification field.",
              )
            }
            if (analysisResult && analysisResult.indicator_name) {
              const {
                indicator_number: num,
                indicator_name: name,
                rating_label: ratingLabel,
                reasoning,
                filename,
              } = analysisResult
              const ratingNote = ratingLabel
                ? ` (suggested rating: **${ratingLabel}**)`
                : ""
              const botContent = `I analyzed **${
                filename || "your file"
              }** and it best fits:\n\n**Indicator ${num} — ${name}**${ratingNote}\n\n${
                reasoning || ""
              }`
              pushMessage({ role: "bot", content: botContent, isDefault: false })
            }
            setConfirmedOffPageModule(null)
            window.dispatchEvent(
              new CustomEvent("evidenceUploaded", {
                detail: { indicator: freshIndicator },
              }),
            )
          },
        )
      },
      () => {
        setIsUploadingEvidence(false)
        message.error("Failed to upload evidence. Please try again.")
      },
    )
  }

  const createDraftRubricAndOpenUpload = () => {
    setIsCreatingRubric(true)
    fetch("/sustainability_rubrics/new")
      .then((response) => {
        const match = response.url.match(/\/sustainability_rubrics\/(\d+)/)
        const newRubricId = match ? parseInt(match[1], 10) : null
        setIsCreatingRubric(false)
        if (!newRubricId) {
          setConfirmedOffPageModule(null)
          message.error(
            "Couldn't create a new rubric for this year. Please try again from the Rubrics menu.",
          )
          return
        }
        setLocallyCreatedDraftRubricId(newRubricId)
        if (evidenceFileInputRef.current) {
          evidenceFileInputRef.current.click()
        }
      })
      .catch(() => {
        setIsCreatingRubric(false)
        setConfirmedOffPageModule(null)
        message.error(
          "Couldn't create a new rubric for this year. Please try again from the Rubrics menu.",
        )
      })
  }

  const proceedWithUpload = () => {
    if (isProgramMatrixModule) {
      if (!areGradesAndPopulationsConfigured()) return
      if (evidenceFileInputRef.current) {
        evidenceFileInputRef.current.click()
      }
      return
    }
    if (isRubricSubmitted && !effectiveDraftRubricId) {
      if (!canCreateRubric) {
        message.warning(
          "This year's rubric has already been submitted. Unsubmit it from the Rubrics menu if you need to add more evidence.",
        )
        return
      }
      createDraftRubricAndOpenUpload()
      return
    }
    if (evidenceFileInputRef.current) {
      evidenceFileInputRef.current.click()
    }
  }

  const handleAttachmentClick = () => {
    if (isCreatingRubric) return
    if (!isSustainabilityModule && !isProgramMatrixModule) {
      // Off-page: we don't know the target module yet, so there's nothing to pre-check here.
      // The file input's onChange handles classification/routing once a file is actually picked.
      if (evidenceFileInputRef.current) {
        evidenceFileInputRef.current.click()
      }
      return
    }
    if (isRubricSubmitted) {
      setShowSubmittedUploadConfirm(true)
      return
    }
    proceedWithUpload()
  }

  const handleUploadAnyway = () => {
    setShowSubmittedUploadConfirm(false)
    proceedWithUpload()
  }

  // Off-page routing target: Sustainability. Mirrors proceedWithUpload's "ensure a draft exists"
  // logic, but resolves the draft/can-create state from the API (current_context) instead of the
  // localStorage/rubricStatusUpdate values a page visit would normally have already populated.
  const routeToSustainability = (file) => {
    APIClient.get(
      "/sustainability_rubrics/current_context",
      (response) => {
        if (response.school_selection_required) {
          pushMessage({
            role: "bot",
            content: "Please select a school from the dropdown at the top of the page first, so I know which Sustainability Rubric to use.",
          })
          return
        }

        const draftRubricId = response.draft_rubric_id
        const isSubmitted = !draftRubricId
        // Feeds the SAME event ChatBotLauncher already listens for on every page (normally
        // dispatched by SustainabilityRubric/Index.jsx on mount) — updates isRubricSubmitted/
        // currentDraftRubricId/canCreateRubric without needing any new props threaded through.
        window.dispatchEvent(new CustomEvent("rubricStatusUpdate", {
          detail: { isSubmitted, draftRubricId, canCreate: response.can_create_rubric },
        }))
        setConfirmedOffPageModule("sustainability")

        if (isSubmitted && !response.can_create_rubric) {
          pushMessage({
            role: "bot",
            content: "This year's Sustainability Rubric has already been submitted. Unsubmit it from the Rubrics menu if you need to add more evidence.",
          })
          setConfirmedOffPageModule(null)
          return
        }
        if (isSubmitted) {
          createDraftRubricAndOpenUpload()
          return
        }
        setEvidenceInitialFile(file)
        setShowUploadModal(true)
      },
      () => {
        pushMessage({
          role: "bot",
          content: "Sorry, I couldn't check your Sustainability Rubric status. Please try attaching the file again.",
        })
      },
    )
  }

  // Off-page routing target: Program Matrix. Mirrors ChatBotUI's areGradesAndPopulationsConfigured
  // gate, resolved via the API since window.gon.CURRENT_SCHOOL_PROGRAM_MATRIX is only populated on
  // the Program Matrix page itself.
  const routeToProgramMatrix = (file) => {
    APIClient.get(
      "/program_matrix/attachment_context",
      (response) => {
        if (response.school_selection_required) {
          pushMessage({
            role: "bot",
            content: "Please select a school from the dropdown at the top of the page first, so I know which Program Matrix to use.",
          })
          return
        }
        if (!response.configured) {
          pushMessage({
            role: "bot",
            content: "Before I can process Program Matrix documents, please configure your school's Grade Levels and populations first.",
          })
          return
        }
        // Same gon values ProgramMatricesController#program_matrix_settings sets on-page —
        // wizardMessageBuilders' domain-options list, flattenGonSelStandards' indicator catalog,
        // and schoolGradeKeys'/schoolGradePopulations' configured-grades list all read straight
        // off window.gon, so without this an off-page attach silently degrades to Rei's single
        // suggested domain and no indicator catalog to pick another indicator from.
        window.gon = window.gon || {}
        window.gon.CATEGORIES = response.categories
        window.gon.SEL_STANDARDS = response.sel_standards
        window.gon.CURRENT_SCHOOL_PROGRAM_MATRIX = {
          ...(window.gon.CURRENT_SCHOOL_PROGRAM_MATRIX || {}),
          selected_grades: response.selected_grades,
        }
        setActiveView("chat")
        pendingProgramMatrixFileRef.current = file
        setConfirmedOffPageModule("program_matrix")
        setProgramMatrixAttachTick((tick) => tick + 1)
      },
      () => {
        pushMessage({
          role: "bot",
          content: "Sorry, I couldn't check your Program Matrix setup. Please try attaching the file again.",
        })
      },
    )
  }

  // pmWizard only becomes the real (non-stub) instance once pmWizardActive flips to true on the
  // NEXT render after setConfirmedOffPageModule("program_matrix") — handleFileAttach can't be
  // called in the same tick as the confirm, so the file waits here until that render happens.
  // Also re-runs on programMatrixAttachTick alone (pmWizardActive may already be true from an
  // earlier attach) so every confirmed attach is consumed, not just the first.
  useEffect(() => {
    if (pmWizardActive && pendingProgramMatrixFileRef.current) {
      const file = pendingProgramMatrixFileRef.current
      pendingProgramMatrixFileRef.current = null
      pmWizard.handleFileAttach(file)
    }
  }, [pmWizardActive, programMatrixAttachTick])

  const routeToOffPageModule = (moduleName, file) => {
    if (moduleName === "sustainability") {
      routeToSustainability(file)
    } else if (moduleName === "program_matrix") {
      routeToProgramMatrix(file)
    } else {
      pushMessage({
        role: "bot",
        content: "No problem — right now I can only help route documents to the Sustainability Rubric or Program Matrix. Feel free to attach it again if that changes!",
      })
    }
  }

  // Off-page entry point: classify the file before running either module's own (heavier)
  // analysis, then let the user confirm/correct Rei's guess.
  const classifyAndRouteUpload = (file) => {
    setActiveView("chat")

    // The extension already deterministically rules one module out (see EXTENSION_MODULE_MAP) —
    // trust that over a GPT content read, which can be misled by a file that merely talks about
    // a module (e.g. a PDF guide *about* Program Matrix) without actually being usable by it.
    const extensionModule = getModuleForExtension(file.name)
    if (extensionModule) {
      pendingClassificationFileRef.current = file
      const otherModule = extensionModule === "sustainability" ? "program_matrix" : "sustainability"
      pushMessage({
        role: "bot",
        type: "single_choice",
        content: `This looks like it's for your **${MODULE_LABELS[extensionModule]}** — is that right?`,
        wizardMeta: {
          moduleClassification: true,
          options: [
            { value: extensionModule, label: `Yes, ${MODULE_LABELS[extensionModule]}`, style: "primary" },
            { value: otherModule, label: `No, it's for ${MODULE_LABELS[otherModule]}`, style: "outline" },
          ],
        },
      })
      return
    }

    setIsClassifyingUpload(true)

    const formData = new FormData()
    formData.append("file", file)

    APIClient.post(
      "/evidence/classify_document_module",
      formData,
      (response) => {
        setIsClassifyingUpload(false)
        const guessedModule = response.module
        pendingClassificationFileRef.current = file

        if (guessedModule === "unknown") {
          pushMessage({
            role: "bot",
            type: "single_choice",
            content: `I couldn't tell what **${file.name}** is for — which of these did you mean?`,
            wizardMeta: {
              moduleClassification: true,
              options: [
                { value: "sustainability", label: "Sustainability Rubric", style: "outline" },
                { value: "program_matrix", label: "Program Matrix", style: "outline" },
              ],
            },
          })
          return
        }

        const otherModule = guessedModule === "sustainability" ? "program_matrix" : "sustainability"
        pushMessage({
          role: "bot",
          type: "single_choice",
          content: `This looks like it's for your **${MODULE_LABELS[guessedModule]}** — ${response.reasoning || "is that right?"}`,
          wizardMeta: {
            moduleClassification: true,
            options: [
              { value: guessedModule, label: `Yes, ${MODULE_LABELS[guessedModule]}`, style: "primary" },
              { value: otherModule, label: `No, it's for ${MODULE_LABELS[otherModule]}`, style: "outline" },
            ],
          },
        })
      },
      () => {
        setIsClassifyingUpload(false)
        pushMessage({
          role: "bot",
          content: "Sorry, I couldn't check that file. Please try attaching it again.",
        })
      },
    )
  }

  // On-page entry point: file was attached while already on the Sustainability Rubric or Program
  // Matrix page, so the page itself is a reasonable guess — ask for confirmation instead of paying
  // for content classification, but still let the user redirect to the other module. The file's
  // extension overrides the page guess when it deterministically rules the page's module out
  // (e.g. attaching a PDF while on the Program Matrix page — that can only be Sustainability).
  const askPageModuleConfirmation = (pageModule, file) => {
    setActiveView("chat")
    pendingClassificationFileRef.current = file
    const guessedModule = getModuleForExtension(file.name) || pageModule
    const otherModule = guessedModule === "sustainability" ? "program_matrix" : "sustainability"
    pushMessage({
      role: "bot",
      type: "single_choice",
      content: `This looks like it's for your **${MODULE_LABELS[guessedModule]}** — is that right?`,
      wizardMeta: {
        moduleClassification: true,
        options: [
          { value: guessedModule, label: `Yes, ${MODULE_LABELS[guessedModule]}`, style: "primary" },
          { value: otherModule, label: `No, it's for ${MODULE_LABELS[otherModule]}`, style: "outline" },
        ],
      },
    })
  }

  // Handles the confirm/change response for a module-classification message — routed here
  // (instead of pmWizard.onWizardRespond) by the moduleClassification flag on its wizardMeta.
  // userMessageContent defaults to the clicked option's label, but handleSend passes the user's
  // own typed text instead when the card was answered by free text (see resolveModuleFromFreeText).
  const handleModuleClassificationRespond = (index, payload, userMessageContent = payload.label) => {
    const pendingFile = pendingClassificationFileRef.current
    pendingClassificationFileRef.current = null
    setMessages((prev) => prev.map((m, i) => (i === index ? { ...m, answered: true } : m)))
    pushMessage({ role: "user", content: userMessageContent })
    if (!pendingFile) return

    // Confirmed module matches the page we're already on — use the direct pipeline (no extra
    // context round-trip) instead of routeToOffPageModule's API-based lookup.
    if (payload.value === "program_matrix" && isProgramMatrixModule) {
      setConfirmedOffPageModule("program_matrix")
      setActiveView("chat")
      pmWizard.handleFileAttach(pendingFile)
      return
    }
    if (payload.value === "sustainability" && isSustainabilityModule) {
      setConfirmedOffPageModule("sustainability")
      setEvidenceInitialFile(pendingFile)
      setShowUploadModal(true)
      return
    }
    routeToOffPageModule(payload.value, pendingFile)
  }
  handleModuleClassificationRespondRef.current = handleModuleClassificationRespond

  const handleVoiceTranscription = (transcription) => {
    // Append transcription to current user input
    setUserInput((prev) => {
      const newText = prev.trim() ? `${prev} ${transcription}` : transcription
      return newText
    })
  }

  const handleCopyMessage = async (content, messageId) => {
    trackChatBot("message_copied")
    try {
      await navigator.clipboard.writeText(content)
      setCopiedMessageId(messageId)
      setShowCopiedTooltip((prev) => ({ ...prev, [messageId]: true }))

      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedMessageId(null)
        setShowCopiedTooltip((prev) => ({ ...prev, [messageId]: false }))
      }, 1000)
    } catch (err) {
      console.error("Failed to copy message: ", err)

      // Fallback for older browsers
      const textArea = document.createElement("textarea")
      textArea.value = content
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand("copy")
      document.body.removeChild(textArea)

      setCopiedMessageId(messageId)
      setShowCopiedTooltip((prev) => ({ ...prev, [messageId]: true }))
      setTimeout(() => {
        setCopiedMessageId(null)
        setShowCopiedTooltip((prev) => ({ ...prev, [messageId]: false }))
      }, 3000)
    }
  }

  const isLastUserMessage = (index) => {
    if (messages[index].role !== "user") return false

    // Find the last user message index
    let lastUserIndex = -1
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === "user") {
        lastUserIndex = i
        break
      }
    }
    return index === lastUserIndex
  }
  const handleEditMessage = (index) => {
    const message = messages[index]
    if (message.role === "user" && isLastUserMessage(index)) {
      setEditingMessageIndex(index)
      setEditText(message.content)
    }
  }

  const handleSaveEdit = () => {
    if (editingMessageIndex === null || !editText.trim()) return

    const originalMessage = messages[editingMessageIndex]

    // Check if the content actually changed
    if (originalMessage.content.trim() === editText.trim()) {
      // No changes made, just exit edit mode
      setEditingMessageIndex(null)
      setEditText("")
      return
    }

    const updatedMessages = [...messages]

    // Update the user message
    updatedMessages[editingMessageIndex] = {
      ...updatedMessages[editingMessageIndex],
      content: editText.trim(),
      edited: true,
    }

    // Mark as edited
    setEditedMessages(new Set([...editedMessages, editingMessageIndex]))

    // Remove the bot's response to the original message
    if (
      editingMessageIndex + 1 < updatedMessages.length
      && updatedMessages[editingMessageIndex + 1].role === "bot"
    ) {
      updatedMessages.splice(editingMessageIndex + 1, 1)
    }

    setMessages(updatedMessages)
    setEditingMessageIndex(null)
    setEditText("")

    // Generate bot response for the edited message without creating a new user message
    setIsLoading(true)
    setHasNoResponse(false)

    // Retrieve conversationId from localStorage
    let conversationId = null
    try {
      const idKey = "conversation_id"
      const storedId = localStorage.getItem(idKey)
      if (storedId) conversationId = storedId
    } catch (e) {
      console.warn("Failed to parse conversationId from localStorage:", e)
    }

    // Build the conversation with the updated message
    const conversationForApi = updatedMessages
      .filter((msg) => msg.role !== "system")
      .map((msg) => ({
        role: msg.role === "bot" ? "assistant" : msg.role,
        content: msg.content,
      }))

    // Determine endpoint based on current context
    const { component, rubric_id: rubricId } = parseRubricUrl()
    const isProgramMatrices = component === "program_matrices"
    const endpoint = (component && component !== "program_matrices") || isProgramMatrices
      ? "/semantic_search_SEL"
      : "/semantic_search"

    // Build payload
    let payload
    if (isProgramMatrices) {
      payload = {
        query: editText.trim(),
        component: category === "general" ? component : category,
        conversation_history: conversationForApi,
        conversation_id: conversationId,
      }
    } else if (component && rubricId) {
      payload = {
        query: editText.trim(),
        component: category === "general" ? component : category,
        rubric_id: rubricId,
        conversation_history: conversationForApi,
        conversation_id: conversationId,
      }
    } else {
      payload = {
        query: editText.trim(),
        temperature: 0.6,
        conversation_history: conversationForApi,
        conversation_id: conversationId,
      }
    }

    // Make API call for bot response
    APIClient.post(
      endpoint,
      payload,
      (response) => {
        setIsLoading(false)

        if (response && response.answer) {
          if (response.is_chunked && response.chunked_response) {
            // Handle chunked response with typing animation
            response.chunked_response.forEach((chunk, index) => {
              setTimeout(() => {
                const botResponse = {
                  role: "bot",
                  content: "",
                  fullContent: chunk,
                  isDefault: false,
                  chunkIndex: index,
                }

                setMessages((prev) => {
                  const newMessages = [...prev, botResponse]
                  animateTyping(chunk, newMessages.length - 1)
                  return newMessages
                })
              }, index * 1200)
            })
          } else {
            // Handle single response with typing animation
            let botResponse
            if (
              response.answer.includes(
                "Contact Support: rsptech@urbanassembly.org",
              )
            ) {
              botResponse = {
                role: "bot",
                content: response.answer,
                isDefault: true,
              }
              setHasNoResponse(true)
              setMessages((prev) => [...prev, botResponse])
            } else {
              botResponse = {
                role: "bot",
                content: "",
                fullContent: response.answer,
                isDefault: false,
              }

              setMessages((prev) => {
                const newMessages = [...prev, botResponse]
                animateTyping(response.answer, newMessages.length - 1)
                return newMessages
              })
            }
          }
        } else {
          const errorResponse = {
            role: "bot",
            content: `I couldn't find information about "${editText.trim()}". Please try another question.`,
            isDefault: true,
          }
          setMessages((prev) => [...prev, errorResponse])
          setHasNoResponse(true)
        }

        // Save to appropriate localStorage
        if (response.updated_conversation) {
          const filteredConversation = filterAndConvertMessages(
            response.updated_conversation,
          )
          const storageKey = "conversation_history"

          localStorage.setItem(
            storageKey,
            JSON.stringify(filteredConversation),
          )
        }

        if (response.conversation_id) {
          const idKey = "conversation_id"
          localStorage.setItem(idKey, response.conversation_id)
        }
      },
      (error) => {
        console.error("API error:", error)
        const errorResponse = {
          role: "bot",
          content:
            "Sorry, I'm having trouble connecting to the knowledge base. Please try again later.",
          isDefault: true,
        }
        setMessages((prev) => [...prev, errorResponse])
        setHasNoResponse(true)
        setIsLoading(false)
      },
    )
  }

  // Check if bot is generating response
  const isBotGenerating = isLoading || isTypingAnimation

  const loadingOptions = {
    animationData: Loading,
    loop: true,
    autoplay: true,
  }

  const sadOptions = {
    animationData: sad,
    loop: true,
    autoplay: true,
  }
  // const handleBack = () => {
  //   setActiveView("initial");
  // };

  // const handleNewChat = () => {
  //   setMessages([]);
  //   setUserInput("");
  //   setIsLoading(false);
  //   setActiveView("initial");
  //   setTermsAccepted(false);
  //   setHasNoResponse(false);
  // };

  const handleContinue = () => {
    if (termsAccepted || hasAcceptedTermsBefore) {
      if (!hasAcceptedTermsBefore) {
        localStorage.setItem("uaAcceptedTerms", "true")
        setHasAcceptedTermsBefore(true)
      }
      setActiveView("initial")
    }
  }

  const handleTermsToggle = () => {
    setHasInteracted(true)
    setTermsAccepted(!termsAccepted)
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages, isLoading, hasNoResponse])

  const markdownComponents = {
    p: ({ children }) => (
      <div
        className="compact-text"
        style={{ margin: "2px 0", lineHeight: 1.3 }}
      >
        {children}
      </div>
    ),
    ul: ({ children }) => (
      <div className="text-block" style={{ margin: "2px 0" }}>
        {children}
      </div>
    ),
    ol: ({ children }) => (
      <div className="text-block" style={{ margin: "2px 0" }}>
        {children}
      </div>
    ),
    h3: ({ children }) => (
      <h5 className="text-block" style={{ margin: "4px 0" }}>
        {children}
      </h5>
    ),
    li: ({ children, ordered, index }) => (
      <div className="inline-text" style={{ margin: 0, lineHeight: 1.4 }}>
        {ordered && `${index + 1}. `}
        {children}
      </div>
    ),
    br: () => null,
    strong: ({ children }) => (
      <strong style={{ fontWeight: 700 }}>{children}</strong>
    ),
    a: ({ href, children }) => {
      if (href && href.includes("/program_matrix")) {
        return (
          <a
            href={href}
            onClick={(e) => {
              e.preventDefault()
              handleActivityNavigation(href)
            }}
            style={{
              color: "#007bff",
              textDecoration: "underline",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {children}
          </a>
        )
      }
      return (
        <a href={href} style={{ color: "#007bff" }}>
          {children}
        </a>
      )
    },
  }
  const textareaRef = useRef(null)

  useEffect(() => {
    if (editingMessageIndex !== null && textareaRef.current) {
      textareaRef.current.focus()
    }
  }, [editingMessageIndex])
  return (
    <div className="chatbot-container">
      {activeView === "welcome" ? (
        <div className="welcome-screen">
          <div className="welcome-content">
            <div className="welcome-message">
              <span className="welcome-text">{commonStrings.welcomeAI} </span>
              <span className="welcome-infor-text">
                {commonStrings.welcomeAIdescription}
              </span>
              <span className="ask-me-text"> {commonStrings.askAnything}</span>
            </div>
            <div className="ai-icon">
              <Lottie
                options={{
                  animationData: hasAcceptedTermsBefore
                    ? staticAnimation
                    : HiAnimation,
                  loop: true,
                  autoplay: true,
                }}
                height={320}
                width={420}
              />
            </div>
            {!hasAcceptedTermsBefore && (
              <div className="terms-container">
                <label className="terms-checkbox">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={handleTermsToggle}
                  />
                  <span className="term-condition-text">
                    Terms & Conditions
                  </span>
                </label>
              </div>
            )}

            {hasAcceptedTermsBefore ? (
              <div className="input-container-wrapper">
                <div className="chatbot-input-container">
                  <button
                    className="attachment-button"
                    onClick={handleAttachmentClick}
                    disabled={isAttachmentDisabled}
                    title={attachmentButtonTitle}
                    type="button"
                  >
                    <PaperClipOutlined />
                  </button>
                  <div className="chatbot-input-wrapper">
                    <input
                      type="text"
                      placeholder="Ask me anything..."
                      className="chatbot-input"
                      value={userInput}
                      onChange={(e) => {
                        setUserInput(e.target.value)
                      }}
                      onKeyDown={(e) => e.key === "Enter" && handleSend()}
                    />
                    <div className="chatbot-input-icons">
                      <VoiceInput
                        onTranscriptionComplete={handleVoiceTranscription}
                        onRecordingStateChange={(recording) => {
                          setIsVoiceRecording(recording)
                        }}
                        disabled={isLoading || isTypingAnimation}
                        disabledTooltipMessage="Voice Input Disabled During AI Response Generation"
                      />
                    </div>
                    <button
                      onClick={handleSend}
                      className="send-icon-button"
                      disabled={isVoiceRecording}
                    >
                      <span className="input-icon">
                        <img src={send} alt="AI send" />
                      </span>
                    </button>
                  </div>
                </div>
                {/* Recording Status - Show when actively recording (below textarea) */}
                {isVoiceRecording && (
                  <div className="indicator-recording-status">
                    <img
                      src={recording}
                      alt="Recording"
                      className="indicator-recording-dot"
                    />
                    <span>Recording...</span>
                  </div>
                )}
              </div>
            ) : (
              <button
                className={`continue-button ${
                  !termsAccepted ? "disabled" : ""
                }`}
                onClick={handleContinue}
                disabled={!termsAccepted}
              >
                Continue
              </button>
            )}
          </div>
        </div>
      ) : activeView === "initial" ? (
        <>
          <div className="main-section-aichatBot">
            <div className="section-aichatBot">
              <div className="section-icon">
                <img src={aboutUA} alt="AI aboutUA" className="chat-icon" />
              </div>
              <div className="section-title">About UA</div>
              <div className="question-buttons">
                {predefinedQuestions.about.map((question, i) => (
                  <button
                    key={i}
                    className="quick-question"
                    onClick={() => handleQuestionClick(question)}
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>

            <div className="section-aichatBot">
              <div className="section-icon">
                <img src={exploreUA} alt="AI exploreUA" className="chat-icon" />
              </div>
              <div className="section-title">Ask & Explore</div>
              <div className="question-buttons">
                {predefinedQuestions.explore.map((question, i) => (
                  <button
                    key={i}
                    className="quick-question"
                    onClick={() => handleQuestionClick(question)}
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="input-container-wrapper">
            <div className="input-container-wrapper">
              <div className="chatbot-input-container">
                <button
                  className="attachment-button"
                  onClick={handleAttachmentClick}
                  disabled={isAttachmentDisabled}
                  title={attachmentButtonTitle}
                  type="button"
                >
                  <PaperClipOutlined />
                </button>
                <div className="chatbot-input-wrapper">
                  <input
                    type="text"
                    placeholder="Ask me anything..."
                    className="chatbot-input"
                    value={userInput}
                    onChange={(e) => {
                      setUserInput(e.target.value)
                    }}
                    onKeyDown={(e) => e.key === "Enter" && handleSend()}
                  />
                  <div className="chatbot-input-icons">
                    <VoiceInput
                      onTranscriptionComplete={handleVoiceTranscription}
                      onRecordingStateChange={(recording) => {
                        setIsVoiceRecording(recording)
                      }}
                      disabled={isLoading || isTypingAnimation}
                      disabledTooltipMessage="Voice Input Disabled During AI Response Generation"
                    />
                  </div>
                  <button
                    onClick={handleSend}
                    className="send-icon-button"
                    disabled={isVoiceRecording}
                  >
                    <span className="input-icon">
                      <img src={send} alt="AI send" />
                    </span>
                  </button>
                </div>
              </div>
              {isVoiceRecording && (
                <div className="indicator-recording-status">
                  <img
                    src={recording}
                    alt="Recording"
                    className="indicator-recording-dot"
                  />
                  <span>Recording...</span>
                </div>
              )}
            </div>
          </div>
        </>
      ) : activeView === "conversations" ? (
        <ConversationHistory
          setActiveView={setActiveView}
          setConvMessages={setConvMessages}
          convMessages={convMessages}
        />
      ) : (
        <>
          {/* <div className="chat-history">
            <div className="back-button" onClick={handleBack}>
              <span className="input-icon-back">
                <img src={backArrow} alt="AI send" className="back-icon" />
                Back
              </span>
            </div>

            <div className="new-chat-button" onClick={handleNewChat}>
              + New chat
            </div>
          </div> */}

          <div className="messages-container">
            <div className="chatbot-messages">
              {messages.map((msg, index) => (
                <div key={index} className={`message ${msg.role}`}>
                  {msg.type ? (
                    <div className="bot-message">
                      <div className="bot-content">
                        <WizardMessage
                          msg={msg}
                          onRespond={(payload) => (
                            msg.wizardMeta && msg.wizardMeta.moduleClassification
                              ? handleModuleClassificationRespond(index, payload)
                              : pmWizard.onWizardRespond(index, {
                                ...payload,
                                wizardField:
                                    msg.wizardMeta && msg.wizardMeta.field,
                                wizardActivityRef:
                                    msg.wizardMeta && msg.wizardMeta.activityRef,
                              })
                          )}
                        />
                      </div>
                    </div>
                  ) : msg.role === "bot" ? (
                    <div className="bot-message">
                      <div className="bot-content">
                        <ReactMarkdown components={markdownComponents}>
                          {
                            isTypingAnimation && typingMessageIndex === index
                              ? `${displayText}|` // Show animated text with typing cursor
                              : msg.fullContent || msg.content // Use fullContent if available, fallback to content
                          }
                        </ReactMarkdown>

                        <Tooltip
                          title="Copied!"
                          open={!!showCopiedTooltip[index]}
                        >
                          <button
                            className="copy-button"
                            onClick={() => handleCopyMessage(
                              msg.fullContent || msg.content,
                              index,
                            )}
                          >
                            {copiedMessageId === index ? (
                              <img
                                src={copiedImage}
                                alt="Copied"
                                className="copy-icon copied"
                              />
                            ) : (
                              <img
                                src={copyImage}
                                alt="Copy message"
                                className="copy-icon"
                              />
                            )}
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`user-message${
                        msg.fileAttachment ? " user-message--file" : ""
                      }`}
                    >
                      {editingMessageIndex === index ? (
                        <div className="edit-mode">
                          <textarea
                            ref={textareaRef}
                            className="edit-textarea"
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault()
                                handleSaveEdit()
                              } else if (e.key === "Escape") {
                                handleCancelEdit()
                              }
                            }}
                          />
                          <div className="edit-buttons">
                            <button
                              className="save-edit-btn"
                              onClick={handleSaveEdit}
                              disabled={!editText.trim()}
                            >
                              Save
                            </button>
                            <button
                              className="cancel-edit-btn"
                              onClick={handleCancelEdit}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {msg.fileAttachment ? (
                            <div className="file-attachment-card">
                              <img
                                src={csvIcon}
                                alt="file"
                                className="file-attachment-card__icon"
                              />
                              <div className="file-attachment-card__details">
                                <div className="file-attachment-card__name">
                                  {msg.fileAttachment.name}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="message-content">
                              {msg.content}
                              {editedMessages.has(index) && (
                                <span className="edited-indicator">
                                  {" "}
                                  (Edited)
                                </span>
                              )}
                            </div>
                          )}
                          {!msg.fileAttachment && (
                            <div className="message-actions">
                              <div>
                                <Tooltip
                                  title="Copied!"
                                  open={!!showCopiedTooltip[index]}
                                >
                                  <button
                                    className="copy-button user-copy"
                                    onClick={() => handleCopyMessage(msg.content, index)}
                                  >
                                    {copiedMessageId === index ? (
                                      <img
                                        src={copiedImage}
                                        alt="Copied"
                                        className="copy-icon copied"
                                      />
                                    ) : (
                                      <img
                                        src={copyImage}
                                        alt="Copy message"
                                        className="copy-icon"
                                      />
                                    )}
                                  </button>
                                </Tooltip>
                              </div>
                              {isLastUserMessage(index) && !isBotGenerating && (
                                <button
                                  className="edit-button"
                                  onClick={() => handleEditMessage(index)}
                                  title="Edit message"
                                >
                                  <img
                                    src={editImage}
                                    alt="Edit message"
                                    className="edit-icon"
                                  />
                                </button>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {(isLoading || pmWizard.isBusy || isClassifyingUpload) && (
                <div className="message bot">
                  <div className="bot-message-loading">
                    <Lottie options={loadingOptions} height={120} width={120} />
                    <div className="loader-css" />
                  </div>
                </div>
              )}
              {hasNoResponse && (
                <div className="message bot">
                  <div className="bot-message">
                    <Lottie options={sadOptions} height={120} width={120} />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>
          <div className="input-container-wrapper">
            <div className="chatbot-input-container">
              <button
                className="attachment-button"
                onClick={handleAttachmentClick}
                disabled={isAttachmentDisabled}
                title={attachmentButtonTitle}
                type="button"
              >
                <PaperClipOutlined />
              </button>
              <div className="chatbot-input-wrapper">
                <input
                  type="text"
                  placeholder="Ask me anything..."
                  className="chatbot-input"
                  value={userInput}
                  onChange={(e) => {
                    setUserInput(e.target.value)
                  }}
                  onKeyDown={(e) => e.key === "Enter" && handleSend()}
                />
                <div className="chatbot-input-icons">
                  <VoiceInput
                    onTranscriptionComplete={handleVoiceTranscription}
                    onRecordingStateChange={(recording) => {
                      setIsVoiceRecording(recording)
                    }}
                    disabled={isLoading || isTypingAnimation}
                    disabledTooltipMessage="Voice Input Disabled During AI Response Generation"
                  />
                </div>
                <button
                  onClick={handleSend}
                  className="send-icon-button"
                  disabled={isVoiceRecording}
                >
                  <span className="input-icon">
                    <img src={send} alt="AI send" />
                  </span>
                </button>
              </div>
            </div>
            {isVoiceRecording && (
              <div className="indicator-recording-status">
                <img
                  src={recording}
                  alt="Recording"
                  className="indicator-recording-dot"
                />
                <span>Recording...</span>
              </div>
            )}
          </div>
        </>
      )}

      {/* Hidden file input — triggered by all paperclip buttons */}
      <input
        ref={evidenceFileInputRef}
        type="file"
        style={{ display: "none" }}
        // Every attach gets asked which module it's for (below) — never routed straight through
        // from a previous confirmation — so the picker always allows either module's file types.
        accept=".pdf,.jpg,.jpeg,.png,.gif,.csv,.xls,.xlsx"
        onChange={(e) => {
          const file = e.target.files && e.target.files[0]
          if (file) {
            if (isProgramMatrixModule || isSustainabilityModule) {
              askPageModuleConfirmation(isProgramMatrixModule ? "program_matrix" : "sustainability", file)
            } else {
              classifyAndRouteUpload(file)
            }
          }
          e.target.value = ""
        }}
      />

      <FileUploadModal
        visible={showUploadModal}
        onClose={() => {
          setShowUploadModal(false)
          setEvidenceInitialFile(null)
          setConfirmedOffPageModule(null)
        }}
        onSubmit={handleEvidenceSubmit}
        isLoading={isUploadingEvidence}
        initialFile={evidenceInitialFile}
        rubricId={targetRubricId}
      />

      <Modal
        open={showSubmittedUploadConfirm}
        onCancel={() => setShowSubmittedUploadConfirm(false)}
        centered
        closable={false}
        width={440}
        className="submitted-upload-confirm-modal"
        footer={[
          <Button
            key="cancel"
            onClick={() => setShowSubmittedUploadConfirm(false)}
            style={{
              height: 40,
              padding: "0 20px",
              borderRadius: 6,
              border: "1.5px solid #d0d0d0",
              color: "#333",
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>,
          <Button
            key="upload-anyway"
            onClick={handleUploadAnyway}
            style={{
              height: 40,
              padding: "0 20px",
              borderRadius: 6,
              background: "#a6192e",
              borderColor: "#a6192e",
              color: "#fff",
              fontWeight: 600,
            }}
          >
            {willCreateNewRubric ? "Create Rubric & Upload" : "Upload Anyway"}
          </Button>,
        ]}
      >
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
          <ExclamationCircleFilled
            style={{
              fontSize: 24,
              color: "#a6192e",
              marginTop: 2,
              flexShrink: 0,
            }}
          />
          <div>
            <h4
              style={{
                margin: "0 0 8px",
                fontSize: 16,
                fontWeight: 700,
                color: "#1a1a2e",
              }}
            >
              Viewing a submitted rubric
            </h4>
            <p
              style={{
                margin: 0,
                fontSize: 14,
                color: "#5a6a7a",
                lineHeight: 1.6,
              }}
            >
              {willCreateNewRubric ? (
                <>
                  You&apos;re viewing a previously submitted rubric, and a
                  rubric for the current year hasn&apos;t been started yet.
                  Uploading a file here will{" "}
                  <strong style={{ color: "#3a4a5a" }}>
                    create a new rubric for the current year
                  </strong>{" "}
                  and add the file to it, not this submitted rubric.
                </>
              ) : (
                <>
                  You&apos;re viewing a previously submitted rubric. Any files
                  you upload will be added to{" "}
                  <strong style={{ color: "#3a4a5a" }}>
                    the current year&apos;s rubric
                  </strong>
                  , not this submitted rubric.
                </>
              )}
            </p>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default ChatBotUI
